import { Card, Grid, Loader, SelectField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";

import {
  getEquipment,
  maintenanceRoutinesByCompanyID,
} from "graphql/queries";
import { globalStrings } from "config/translations/es";

const EquipmentRoutines = ({
  initialValues,
  onChange: onChangeRoutines,
  companyID,
}) => {
  const [routinesSelected, setRoutinesSelected] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(false);

  const onSelect = (equipmentID, value) => {
    setRoutinesSelected((prevSt) => {
      const listUpdated = [...prevSt];
      const index = listUpdated.findIndex(
        (it) => it.equipmentID === equipmentID
      );
      listUpdated[index] = {
        ...listUpdated[index],
        routineID: value,
      };
      if (onChangeRoutines) onChangeRoutines(listUpdated);
      return listUpdated;
    });
  };

  async function fetchMaintenanceRoutines() {
    try {
      if (!companyID) return;
      setLoading(true);
      const maintenanceRoutinesData = await API.graphql(
        graphqlOperation(maintenanceRoutinesByCompanyID, {
          companyID,
          filter: {
            deletedAt: { attributeExists: false },
          },
        })
      );
      const maintenanceRoutinesItems =
        maintenanceRoutinesData.data.maintenanceRoutinesByCompanyID.items;
      setRoutines(maintenanceRoutinesItems);
    } catch (err) {
      toast.error(strings.responses.errorFetchMaintenanceRoutines);
    } finally {
      setLoading(false);
    }
  }

  async function fetchEquipments(values) {
    try {
      setLoading(true);
      if (!values || values?.length <= 0) {
        setRoutinesSelected(initialValues ?? []);
        return;
      }

      const ids = values.map((val) => val.equipmentID);

      if (
        routinesSelected?.every((routine) =>
          ids.some((id) => id === routine.equipmentID)
        ) &&
        ids?.every((routine) =>
          routinesSelected.some((id) => id === routine.equipmentID)
        )
      )
        return;

      const equipmentItems = [];
      if (ids.length > 0) {
        await Promise.all(
          ids.map(async (id) => {
            const equipmentData = await API.graphql(
              graphqlOperation(getEquipment, { id })
            );
            if (
              equipmentData.data.getEquipment &&
              !equipmentData.data.getEquipment.deletedAt
            ) {
              equipmentItems.push(equipmentData.data.getEquipment);
            }
          })
        );
      }

      setEquipments(equipmentItems);
      setRoutinesSelected(initialValues);
    } catch (err) {
      toast.error(strings.responses.errorFetchEquipments);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchEquipments(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    fetchMaintenanceRoutines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loader />;

  return (
    routinesSelected?.length > 0 && (
      <>
        <div className="fs-5">{strings.title}</div>
        <Grid
          templateColumns={{ base: "1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          {routinesSelected?.map((routineSelected) => {
            const equipmentName =
              equipments?.find((e) => e.id === routineSelected.equipmentID)
                ?.name || "";
            return (
              <Card
                key={routineSelected.equipmentID}
                height="fit-content"
                borderRadius="medium"
                variation="outlined"
              >
                <SelectField
                  label={equipmentName}
                  value={routineSelected.routineID}
                  onChange={(e) =>
                    onSelect(routineSelected.equipmentID, e.target.value)
                  }
                >
                  <option value="">{globalStrings.selectOption}</option>
                  {routines.map((maintenanceRoutine) => (
                    <option
                      key={maintenanceRoutine.id}
                      value={maintenanceRoutine.id}
                    >
                      {maintenanceRoutine.name}
                    </option>
                  ))}
                </SelectField>
              </Card>
            );
          })}
        </Grid>
      </>
    )
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Rutinas de Mantenimiento (Equipos asociados)",
    selectOption: "Seleccione una opción",
    fields: {
      routine: {
        label: "Rutina de Mantenimiento",
      },
    },
    responses: {
      errorFetchMaintenanceRoutines:
        "Error al consultar rutinas de mantenimiento",
      errorFetchEquipments: "Error al consultar equipos",
    },
  },
});

export default EquipmentRoutines;
